import { useState } from "react";
import { Play, Mic, Headphones, ExternalLink } from "lucide-react";
import amiPodcastImage from "@/assets/ami-podcast-danielle.png";

type Clip = {
  id: string;
  title: string;
  type: "video" | "short";
};

const clips: Clip[] = [
  {
    id: "PftoNB-p9K4",
    title: "Full conversation: Wrangling Chaos in agency life",
    type: "video",
  },
  {
    id: "VKtgxMiHgmQ",
    title: "On managing up & protecting your team",
    type: "short",
  },
  {
    id: "gIkjh-VRdH0",
    title: "The real reason projects break",
    type: "short",
  },
];

const amiEpisode = {
  show: "Build a Better Agency",
  host: "Drew McLellan",
  episode: "Episode 559",
  title: "Essential Project Management Skills for Agencies with Danielle McCier",
  url: "https://agencymanagementinstitute.com/podcasts/agency-project-management/",
};

function AudioFeatureCard() {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-soft transition-all hover:shadow-bold">
      <a
        href={amiEpisode.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Listen to ${amiEpisode.title} on Build a Better Agency`}
        className="relative block"
      >
        <img
          src={amiPodcastImage}
          alt={`${amiEpisode.show} episode 559 featuring Danielle McCier, hosted by Drew McLellan`}
          loading="lazy"
          className="h-auto w-full"
        />
      </a>
      <div className="flex flex-col gap-5 p-6 md:p-8">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground">
            <Headphones className="h-3.5 w-3.5" aria-hidden="true" />
            Featured audio interview
          </div>
          <p className="mt-4 text-sm font-semibold uppercase tracking-wide text-brand-yellow-dark">
            {amiEpisode.show} · {amiEpisode.episode}
          </p>
          <h3 className="mt-2 font-display text-xl font-bold leading-snug md:text-2xl">
            {amiEpisode.title}
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Hosted by {amiEpisode.host}, Agency Management Institute. Listen on
            Apple Podcasts, Spotify, or iHeartRadio.
          </p>
        </div>
        <a
          href={amiEpisode.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 self-start rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-yellow transition-transform hover:scale-105"
        >
          Listen to the episode
          <ExternalLink className="h-4 w-4" aria-hidden="true" />
        </a>
      </div>
    </div>
  );
}

function ClipCard({ clip, featured = false }: { clip: Clip; featured?: boolean }) {
  const [playing, setPlaying] = useState(false);
  const isShort = clip.type === "short";
  const aspect = isShort ? "aspect-[9/16]" : "aspect-video";
  const thumb = `https://i.ytimg.com/vi/${clip.id}/hqdefault.jpg`;
  const embed = `https://www.youtube-nocookie.com/embed/${clip.id}?autoplay=1&rel=0`;
  const watchUrl = isShort
    ? `https://youtube.com/shorts/${clip.id}`
    : `https://youtu.be/${clip.id}`;

  return (
    <div
      className={`group overflow-hidden rounded-2xl border border-border bg-card shadow-soft transition-all hover:shadow-bold ${
        featured ? "md:col-span-2" : ""
      }`}
    >
      <div className={`relative ${aspect} w-full bg-secondary`}>
        {playing ? (
          <iframe
            src={embed}
            title={clip.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 h-full w-full"
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label={`Play clip: ${clip.title}`}
            className="group/btn absolute inset-0 h-full w-full"
          >
            <img
              src={thumb}
              alt={clip.title}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-500 group-hover/btn:scale-105"
            />
            <div className="absolute inset-0 bg-secondary/30 transition-colors group-hover/btn:bg-secondary/10" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-yellow transition-transform group-hover/btn:scale-110">
                <Play className="h-7 w-7 fill-current" aria-hidden="true" />
              </div>
            </div>
          </button>
        )}
      </div>
      <div className="flex items-start justify-between gap-3 p-4">
        <p className="text-sm font-medium text-foreground line-clamp-2">{clip.title}</p>
        <a
          href={watchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 text-xs font-semibold text-brand-yellow-dark hover:underline"
        >
          YouTube ↗
        </a>
      </div>
    </div>
  );
}

export function PodcastClips() {
  const [featured, ...shorts] = clips;
  return (
    <section className="bg-muted py-20 md:py-28">
      <div className="container">
        <div className="mx-auto max-w-2xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
            <Mic className="h-4 w-4" aria-label="Podcast" />
            Hear the conversation
          </div>
          <h2 className="mt-4 font-display text-3xl font-bold md:text-4xl">
            On the podcast circuit
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Recent podcast appearances about the book, agency life, and leading
            projects without losing your mind.
          </p>
        </div>

        <div className="mx-auto mt-12 max-w-5xl space-y-6">
          <div className="mx-auto max-w-3xl">
            <AudioFeatureCard />
          </div>
          <div className="mx-auto max-w-3xl">
            <ClipCard clip={featured} featured />
          </div>
          <div className="mx-auto grid max-w-3xl gap-6 sm:grid-cols-2">
            {shorts.map((c) => (
              <ClipCard key={c.id} clip={c} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
